import { useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { UPDATE_PRESCRIBER } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import { useFormData } from 'hooks/useFormData'
import type { Prescriber } from 'types'
import { validateNpi } from 'utils'

interface usePrescriberProps {
  prescriber: Prescriber
}

interface usePrescriberReturn {
  loading: boolean
  savePrescriberChanges: () => Promise<void>
  resetPrescriberChanges: () => void
}

export default function usePrescriber({ prescriber }: usePrescriberProps): usePrescriberReturn {
  const [updatePrescriber, { loading }] = useMutation(UPDATE_PRESCRIBER)
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()

  const {
    changes: { getPrescriberChanges },
    actions: { updateFormData },
  } = useFormData()

  const savePrescriberChanges = useCallback(async () => {
    try {
      const updates = getPrescriberChanges(prescriber) as any

      if (Object.keys(updates).length) {
        if (updates.npi && !validateNpi(updates.npi)) {
          throw new Error('Invalid NPI')
        }

        await updatePrescriber({
          variables: {
            prescriberId: prescriber._id,
            updates,
          },
          refetchQueries: ['getSyncedPrescriber'],
        })

        showSuccessToast('Prescriber successfully updated')
      }
    } catch (error) {
      if (error instanceof Error) {
        showErrorToast('Failed to update prescriber: ' + error.message)
      } else {
        showErrorToast('Failed to update prescriber')
      }
      throw error
    }
  }, [prescriber, getPrescriberChanges, updatePrescriber])

  const resetPrescriberChanges = useCallback(() => {
    updateFormData({ prescriber: { $set: prescriber } })
  }, [prescriber, updateFormData])

  return {
    loading,
    savePrescriberChanges,
    resetPrescriberChanges,
  }
}
